import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type AdActivityDataType, useAdActivityFitScore } from '@/api/ad';
import type { DataTableColumnDef } from '@/components/DataTable';
import {
  FitScoreLegend,
  FitScoreMetricCell,
  FitScoreTable,
  FitScoreTitleCell,
} from '@/components/FitScoreTable';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { SupportLink } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

type Props = {
  keywordIds: string[];
};

const PaidSearchKeywordFitScoringTable = ({ keywordIds }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { fitScoreData, isLoading, error } = useAdActivityFitScore(
    keywordIds,
    startDate,
    endDate,
    audience?.id,
  );

  const columns = useMemo<DataTableColumnDef<AdActivityDataType>[]>(
    () => [
      {
        id: 'name',
        header: t`Keyword`,
        size: 300,
        minSize: 300,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreTitleCell
              title={field.ad.name}
              subtitle={
                <Trans>
                  {field.ad.adGroup.campaign.vendor.name} -{' '}
                  {field.ad.adGroup.campaign.vendor.channel?.name}
                </Trans>
              }
            />
          );
        },
      },
      {
        id: 'allVisits',
        accessorKey: 'allVisits',
        header: t`Visits`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.allVisitsBest}
              isWorst={field.allVisitsWorst}
              label={<Trans>Visits</Trans>}
              value={numberFormat(field.allVisits)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'engagedCompanies',
        accessorKey: 'engagedCompanies',
        header: t`Engaged Companies`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.engagedCompaniesBest}
              isWorst={field.engagedCompaniesWorst}
              label={<Trans>Engaged Companies</Trans>}
              value={numberFormat(field.percentOfEngagedCompanies, {
                isPercent: true,
                precision: 1,
              })}
              caption={numberFormat(field.engagedCompanies)}
              captionLink={ROUTES.webTrafficActivityWithParams({
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'true' : undefined,
                'w.keywordIds': field.ad.id,
                'w.metrics.visits': '[1,]',
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'spendPerVisit',
        accessorKey: 'spendPerTargetVisit',
        header: t`$/Visit`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.spendPerVisitBest}
              isWorst={field.spendPerVisitWorst}
              label={<Trans>$/Visit</Trans>}
              value={numberFormat(field.spendPerTargetVisit, { isCurrency: true, precision: 2 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'pipelineInfluenced',
        accessorKey: 'pipelineInfluencedPerSpend',
        header: t`$/Pipeline Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.pipelineInfluencedBest}
              isWorst={field.pipelineInfluencedWorst}
              label={<Trans>$/Pipeline Influenced</Trans>}
              value={numberFormat(field.pipelineInfluencedPerSpend, {
                isMultiplier: true,
                precision: 1,
              })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'opportunityInfluenced',
        accessorKey: 'percentOpportunitiesInfluenced',
        header: t`% Opps Influenced`,
        minSize: 173,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.opportunityInfluencedBest}
              isWorst={field.opportunityInfluencedWorst}
              label={<Trans>% Opps Influenced</Trans>}
              value={numberFormat(field.percentOpportunitiesInfluenced, {
                isPercent: true,
                precision: 0,
              })}
              caption={numberFormat(field.opportunitiesInfluenced)}
              error={error}
              isLoading={false}
            />
          );
        },
      },
      {
        id: 'fitScore',
        accessorKey: 'fitScore',
        header: t`Fit Score`,
        minSize: 173,
        flex: 1,
        enableSorting: true,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <FitScoreMetricCell
              isBest={field.fitScoreBest}
              isWorst={field.fitScoreWorst}
              label={<Trans>Fit Score</Trans>}
              value={numberFormat(field.fitScore, { precision: 1 })}
              error={error}
              isLoading={false}
            />
          );
        },
      },
    ],
    [audience?.id, audience?.isTAM, error],
  );

  return (
    <Panel
      title={
        <Trans>Fit Score - {audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`)}</Trans>
      }
      infoPopover={{
        title: t`Fit Score`,
        body: (
          <Trans>
            The Keyword Fit Score is an analysis of 5 major metrics used to calculate keyword
            performance from 1-100.{' '}
            <SupportLink variant="body2" color="static-green" path="FIT_SCORE">
              Learn more
            </SupportLink>
          </Trans>
        ),
      }}
      noPadding
    >
      <FitScoreLegend />
      <FitScoreTable<AdActivityDataType>
        columns={columns}
        dataSource={fitScoreData}
        emptyMessage={t`Select keywords in the table above and click the Score button to see your fit scores.`}
        loading={isLoading}
        error={error}
        rowKey={(record) => record.ad.id}
        tableClassName="min-w-[1300px]"
        initialColumnPinning={{ left: ['name'], right: ['fitScore'] }}
        defaultSorting={[{ id: 'fitScore', desc: true }]}
      />
    </Panel>
  );
};

export default PaidSearchKeywordFitScoringTable;
